import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, MessageCircle, Share2, Trophy } from "lucide-react";

interface EngagementQuality {
  commentsToLikesRatio: number;
  sharesToLikesRatio: number;
  avgCommentLength: number;
  qualityScore: number;
  commentsCount: number;
  sharesCount: number;
}

interface EngagementQualityScoreProps {
  engagementQuality: EngagementQuality;
}

export const EngagementQualityScore = ({ engagementQuality }: EngagementQualityScoreProps) => {
  if (!engagementQuality) {
    return null;
  }

  const getScoreLabel = (score: number) => {
    if (score >= 80) return { label: 'Excellent', color: 'text-success' };
    if (score >= 60) return { label: 'Good', color: 'text-primary' };
    if (score >= 40) return { label: 'Average', color: 'text-warning' };
    return { label: 'Needs Improvement', color: 'text-destructive' };
  };

  const scoreInfo = getScoreLabel(engagementQuality.qualityScore);

  const benchmarks = [
    {
      metric: 'Comments/Likes Ratio',
      value: engagementQuality.commentsToLikesRatio,
      benchmark: 0.10,
      icon: MessageCircle,
      description: 'Industry benchmark: 0.10',
      format: (v: number) => v.toFixed(2)
    },
    {
      metric: 'Shares/Likes Ratio',
      value: engagementQuality.sharesToLikesRatio,
      benchmark: 0.03,
      icon: Share2,
      description: 'Industry benchmark: 0.03',
      format: (v: number) => v.toFixed(2)
    },
    {
      metric: 'Avg Comment Length',
      value: engagementQuality.avgCommentLength,
      benchmark: 30,
      icon: MessageCircle,
      description: 'Industry benchmark: 30 characters',
      format: (v: number) => `${Math.round(v)} chars`
    }
  ];

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              <CardTitle>Engagement Quality Score</CardTitle>
            </div>
            <CardDescription>
              Jakość zaangażowania na podstawie komentarzy i udostępnień
            </CardDescription>
          </div>
          
          <div className="text-center">
            <div className={`text-4xl font-bold ${scoreInfo.color}`}>
              {engagementQuality.qualityScore}
            </div>
            <Badge variant="outline" className={scoreInfo.color}>
              {scoreInfo.label}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Progress Bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Quality</span>
            <span className="text-sm text-muted-foreground">
              {engagementQuality.qualityScore}/100
            </span>
          </div>
          <Progress value={engagementQuality.qualityScore} className="h-3" />
        </div>

        {/* Metrics Breakdown */}
        <div className="space-y-4">
          {benchmarks.map((item, index) => {
            const Icon = item.icon;
            const percentage = Math.min((item.value / item.benchmark) * 100, 100);
            const isBelowBenchmark = item.value < item.benchmark;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{item.metric}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">
                      {item.format(item.value)}
                    </span>
                    {!isBelowBenchmark && (
                      <TrendingUp className="h-4 w-4 text-success" />
                    )}
                  </div>
                </div>
                
                <div className="relative">
                  <Progress 
                    value={percentage} 
                    className="h-2"
                  />
                  {/* Benchmark line */}
                  <div 
                    className="absolute top-0 h-2 w-0.5 bg-muted-foreground/50"
                    style={{ left: '100%' }}
                  />
                </div>
                
                <p className="text-xs text-muted-foreground">{item.description}</p>
              </div>
            );
          })}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <MessageCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold">
              {engagementQuality.commentsCount}
            </p>
            <p className="text-xs text-muted-foreground">Total Comments</p>
          </div>
          
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <Share2 className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold">
              {engagementQuality.sharesCount}
            </p>
            <p className="text-xs text-muted-foreground">Total Shares</p>
          </div>
        </div>

        {/* Insight */}
        <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
          <p className="text-sm">
            <span className="font-semibold">💡 Insight:</span>{' '}
            {engagementQuality.qualityScore >= 80 
              ? 'Świetna jakość zaangażowania! Twoje posty generują głębokie interakcje.'
              : engagementQuality.qualityScore >= 60
              ? 'Dobra jakość zaangażowania. Skup się na treściach zachęcających do komentowania.'
              : 'Potrzeba poprawy. Twórz treści, które prowokują dyskusję i są warte udostępnienia.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
